/**
 * Tool risk level values.
 */
export const ToolRiskLevel = {
  Low: 0,
  Medium: 1,
  High: 2,
  Critical: 3,
} as const;

export type ToolRiskLevel = (typeof ToolRiskLevel)[keyof typeof ToolRiskLevel];

/** Matches API enum ToolExecutionLocation (0 = Server, 1 = Client, 2 = Both). */
export const ToolExecutionLocation = {
  Server: 0,
  Client: 1,
  Both: 2,
} as const;

export type ToolExecutionLocation = (typeof ToolExecutionLocation)[keyof typeof ToolExecutionLocation];

/** User-facing label for tool execution location (chips, detail headers). */
export function executionLocationDisplayLabel(loc?: number): string {
  switch (loc) {
    case ToolExecutionLocation.Client:
      return 'Client Only';
    case ToolExecutionLocation.Both:
      return 'Both';
    case ToolExecutionLocation.Server:
    default:
      return 'Server Only';
  }
}

/**
 * Tool list DTO.
 */
export interface ToolDto {
  id: string;
  name: string;
  displayName: string;
  description: string;
  category: string;
  isDefault: boolean;
  isSystem: boolean;
  isActive: boolean;
  riskLevel: ToolRiskLevel;
  requiresApproval: boolean;
  executionLocation: ToolExecutionLocation;
}

/**
 * Tool detail DTO.
 */
export interface ToolDetailDto {
  id: string;
  tenantId: string;
  projectId?: string;
  name: string;
  displayName: string;
  description: string;
  category: string;
  inputSchemaJson: string;
  outputSchemaJson: string;
  isDefault: boolean;
  isActive: boolean;
  isSystem: boolean;
  version: string;
  requiresApproval: boolean;
  riskLevel: ToolRiskLevel;
  executionLocation: ToolExecutionLocation;
  implementationType?: string;
  implementationJson?: string;
  documentationUrl?: string;
  exampleUsageJson?: string;
  metadataJson?: string;
}

/**
 * Tool execution log DTO.
 */
export interface ToolExecutionLogDto {
  id: string;
  agentSessionId?: string;
  toolId: string;
  toolName: string;
  toolKey: string;
  correlationId?: string;
  isDryRun: boolean;
  success: boolean;
  parametersJson?: string;
  resultJson?: string;
  impactPreviewJson?: string;
  errorMessage?: string;
  executionTimeMs: number;
  executedBy?: string;
  reason?: string;
  executedAt: string;
}

/**
 * Filters for tool queries.
 */
export interface ToolFilters {
  pageNumber?: number;
  pageSize?: number;
  category?: string;
  isDefault?: boolean;
  isActive?: boolean;
  searchTerm?: string;
}

/**
 * Request model for creating a tool.
 */
export interface CreateToolRequest {
  name: string;
  displayName: string;
  description: string;
  category: string;
  inputSchemaJson: string;
  outputSchemaJson: string;
  isDefault?: boolean;
  isActive?: boolean;
  isSystem?: boolean;
  version?: string;
  requiresApproval?: boolean;
  riskLevel?: string; // "Low" | "Medium" | "High" | "Critical"
  executionLocation?: ToolExecutionLocation;
  implementationType?: string;
  implementationJson?: string;
  documentationUrl?: string;
  exampleUsageJson?: string;
  metadataJson?: string;
}

/**
 * Request model for updating a tool.
 */
export interface UpdateToolRequest {
  displayName: string;
  description: string;
  category: string;
  inputSchemaJson: string;
  outputSchemaJson: string;
  isDefault: boolean;
  isActive: boolean;
  version: string;
  requiresApproval: boolean;
  riskLevel: string; // "Low" | "Medium" | "High" | "Critical"
  executionLocation: ToolExecutionLocation;
  implementationType?: string;
  implementationJson?: string;
  documentationUrl?: string;
  exampleUsageJson?: string;
  metadataJson?: string;
}

/**
 * Request model for executing/previewing a tool.
 */
export interface ExecuteToolRequest {
  toolName: string;
  parameters: Record<string, any>;
  isDryRun: boolean;
  reason?: string;
}

/**
 * Tool execution result returned by API.
 */
export interface ToolExecutionResult {
  toolCallId?: string;
  toolName: string;
  result: string;
  isSuccess: boolean;
  errorMessage?: string;
  isDryRun?: boolean;
  impactPreviewJson?: string;
  executionTimeMs?: number;
}

/**
 * Tool impact preview returned by API.
 */
export interface ToolImpactPreview {
  summary?: string;
  impactPreviewJson: string;
}

